import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Button, Card, CardContent, CircularProgress, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField
} from '@mui/material'
import { toast } from 'react-toastify'
import { settingsApi, LobSalesExpiryItem } from '../lib/api/settingsApi'
import { PageHeader } from '../components/shared/PageHeader'
import { themeColors } from '../app/layout/theme'
import { usePermission } from '../lib/hooks/usePermission'

export default function SalesExpirySettings() {
  const queryClient = useQueryClient()
  const { hasAction } = usePermission()
  const canUpdate = hasAction('/settings', 'update')

  const [lobValues, setLobValues] = useState<Record<string, string>>({})
  const [savingLobId, setSavingLobId] = useState<string | null>(null)

  // Query settings from API
  const { data: lobItems = [], isLoading } = useQuery({
    queryKey: ['sales-expiry-setting'],
    queryFn: settingsApi.getSalesExpiry,
  })

  // Set local state when query data completes
  useEffect(() => {
    if (lobItems.length > 0) {
      const initialMap: Record<string, string> = {}
      lobItems.forEach(item => {
        initialMap[item.lobId] = String(item.days ?? 0)
      })
      setLobValues(initialMap)
    }
  }, [lobItems])

  // Mutation to update settings for a specific LOB
  const updateMutation = useMutation({
    mutationFn: (payload: { lobId: string; days: number }) => settingsApi.updateSalesExpiry(payload),
    onSuccess: () => {
      toast.success('Sales expiry duration updated successfully')
      queryClient.invalidateQueries({ queryKey: ['sales-expiry-setting'] })
      setSavingLobId(null)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Failed to update settings')
      setSavingLobId(null)
    }
  })

  const handleInputChange = (lobId: string, val: string) => {
    setLobValues(prev => ({ ...prev, [lobId]: val }))
  }

  const handleSaveLob = (item: LobSalesExpiryItem) => {
    const val = lobValues[item.lobId] ?? '0'
    const numericDays = parseInt(val, 10)
    if (isNaN(numericDays) || numericDays < 0) {
      toast.error('Expiry duration must be 0 or a positive integer.')
      return
    }
    setSavingLobId(item.lobId)
    updateMutation.mutate({ lobId: item.lobId, days: numericDays })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  const cardSx = {
    borderRadius: '14px',
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.shadow.sm,
    maxWidth: 750,
  }

  return (
    <Box>
      <PageHeader
        title="LOB Sales Expiry Settings"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb={true}
      />

      <Box sx={{ px: 3, pb: 4 }}>
        <Card sx={cardSx}>
          <CardContent sx={{ p: 4 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary, mb: 1 }}>
              Line of Business Sales Expiry Duration (Days)
            </Typography>
            <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, mb: 3 }}>
              Configure the number of days after which a Sale under each Line of Business (LOB) will automatically expire and move to the Lost stage if no order has been confirmed. A value of 0 means sales under that LOB will never expire.
            </Typography>

            {lobItems.length === 0 ? (
              <Typography sx={{ fontSize: '14px', color: themeColors.text.secondary, fontStyle: 'italic', py: 2 }}>
                No Lines of Business found for your organization.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
                <Table>
                  <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: themeColors.text.primary }}>Line of Business</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: themeColors.text.primary, width: 220 }}>Expiry Duration (Days)</TableCell>
                      {canUpdate && <TableCell align="right" sx={{ fontWeight: 600, color: themeColors.text.primary, width: 120 }}>Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lobItems.map((item) => {
                      const currentVal = lobValues[item.lobId] ?? String(item.days ?? 0)
                      const isItemSaving = savingLobId === item.lobId && updateMutation.isPending
                      const hasChanged = String(item.days) !== currentVal

                      return (
                        <TableRow key={item.lobId} hover>
                          <TableCell sx={{ fontWeight: 500, color: themeColors.text.primary }}>
                            {item.lobName}
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={currentVal}
                              onChange={(e) => handleInputChange(item.lobId, e.target.value)}
                              disabled={!canUpdate || isItemSaving}
                              inputProps={{ min: 0 }}
                              sx={{ width: 140 }}
                            />
                          </TableCell>
                          {canUpdate && (
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveLob(item)}
                                disabled={isItemSaving || !hasChanged}
                                sx={{
                                  bgcolor: themeColors.primary,
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  '&:hover': {
                                    bgcolor: themeColors.primaryDark,
                                  }
                                }}
                              >
                                {isItemSaving ? 'Saving...' : 'Save'}
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
